import React, { useState, useEffect, useRef } from 'react';
import { SVG } from '@svgdotjs/svg.js';

const Contrapisos = () => {
  // 1. Parámetros de diseño con valores iniciales realistas
  const [dimensiones, setDimensiones] = useState({
    ancho: 4.0,       // metros
    espesor: 0.12,    // metros (12 cm)
    recubrimiento: 3, // cm
  });

  const [armadura, setArmadura] = useState({
    separacion: 0.15,  // metros (15 cm)
    diametro: 8,       // mm
  });

  const [materiales, setMateriales] = useState({
    fc: 210,          // kg/cm²
    fy: 4200,         // kg/cm²
  });

  const [cargas, setCargas] = useState({
    viva: 500,        // kg/m²
    muerta: 150,      // kg/m²
  });

  const [suelo, setSuelo] = useState({
    balasto: 8,       // kgf/cm³
  });

  const [zoom, setZoom] = useState(1);
  const canvasRef = useRef(null);
  const menuRef = useRef(null);

  // 2. Resultados de cálculo con valores iniciales
  const [resultados, setResultados] = useState({
    pesoPropio: 0,
    cargaTotal: 0,
    momentoMaximo: 0,
    capacidadMomento: 0,
    relacionCapacidad: 0,
    separacionJuntas: 0,
    estado: "✔ Diseño Adecuado",
    error: null
  });

  // 3. Cálculos principales (optimizados)
  const calcularResultados = () => {
    try {
      // Conversión de unidades
      const h = dimensiones.espesor;
      const b = dimensiones.ancho;
      const recub = dimensiones.recubrimiento / 100; // cm → m
      const ϕ = armadura.diametro / 1000; // mm → m
      const s = armadura.separacion;
      
      // Área de acero por metro
      const As = (Math.PI * Math.pow(ϕ/2, 2)) / s; // m²/m
      
      // Altura efectiva
      const d = h - recub - ϕ/2;
      
      // Bloque de compresiones
      const a = (As * materiales.fy * 10000) / (0.85 * materiales.fc * 10000);
      
      // Capacidad a momento (φMn)
      const φMn = 0.9 * As * materiales.fy * 10000 * (d - a/2); // kg-m/m
      
      // Cargas y momento solicitante (Ma)
      const pesoPropio = 2400 * h;
      const cargaTotal = pesoPropio + cargas.muerta + cargas.viva;
      
      // Modelo de Winkler mejorado
      const k = suelo.balasto * 1000000; // kgf/cm³ → kg/m³
      const E = 15000 * Math.sqrt(materiales.fc) * 10000; // kg/m²
      const I = Math.pow(h, 3) / 12; // m⁴/m
      const λ = Math.pow((k * b) / (4 * E * I), 0.25);
      const Ma = (cargaTotal * b) / (2 * Math.pow(λ, 2)); // kg-m/m
      
      // Juntas de dilatación
      const α = 0.00001; // Coeficiente de dilatación térmica
      const ΔT = 30; // Diferencia de temperatura
      const juntas = Math.min(30 * h * 100, 450 / (α * ΔT * 1000)); // cm
      
      // Relación de capacidad
      const relacion = φMn / Ma;
      
      setResultados({
        pesoPropio: pesoPropio.toFixed(0),
        cargaTotal: cargaTotal.toFixed(0),
        momentoMaximo: Ma.toFixed(2),
        capacidadMomento: φMn.toFixed(2),
        relacionCapacidad: relacion.toFixed(2),
        separacionJuntas: juntas.toFixed(0),
        estado: relacion >= 1.0 ? "✔ Diseño Adecuado" : "✘ Rediseñar",
        error: null
      });

    } catch (error) {
      setResultados(prev => ({
        ...prev,
        error: "Error en cálculos. Verifique los parámetros."
      }));
    }
  };

  // 4. Dibujo del contrapiso (optimizado)
  const dibujarContrapiso = () => {
    if (!canvasRef.current) return;
    
    canvasRef.current.innerHTML = '';
    const draw = SVG().addTo(canvasRef.current).size('100%', '100%').viewbox(0, 0, 1000, 1000);
    
    const scale = 1000 * zoom / dimensiones.ancho;
    const contrapisoWidth = dimensiones.ancho * scale;
    const contrapisoHeight = dimensiones.espesor * scale;
    const sueloHeight = 0.3 * scale;
    const centerX = (1000 - contrapisoWidth) / 2;
    const centerY = (1000 - contrapisoHeight - sueloHeight) / 2;

    // Capa de suelo
    draw.rect(contrapisoWidth, sueloHeight)
       .fill('#8B4513').stroke('#654321', 2)
       .move(centerX, centerY + contrapisoHeight);

    // Contrapiso de hormigón
    draw.rect(contrapisoWidth, contrapisoHeight)
       .fill('#cbd5e0').stroke('#2d3748', 2)
       .move(centerX, centerY);

    // Armadura corregida (posición exacta)
    const numVarillas = Math.ceil(dimensiones.ancho / armadura.separacion);
    const recubPixels = (dimensiones.recubrimiento / 100) * scale;
    const effectiveWidth = contrapisoWidth - 2 * recubPixels;
    const barSpacing = numVarillas > 1 ? effectiveWidth / (numVarillas - 1) : 0;
    const barDiameterPixels = (armadura.diametro / 1000) * scale;

    for (let i = 0; i < numVarillas; i++) {
      draw.circle(barDiameterPixels)
         .fill('#2d3748')
         .move(
           centerX + recubPixels + i * barSpacing - barDiameterPixels/2,
           centerY + contrapisoHeight - recubPixels - barDiameterPixels/2
         );
    }
  };

  // 5. Efectos secundarios
  useEffect(() => {
    calcularResultados();
  }, [dimensiones, armadura, materiales, cargas, suelo]);

  useEffect(() => {
    dibujarContrapiso();
  }, [dimensiones, armadura, zoom, resultados.separacionJuntas]);

  // 6. Manejo de eventos
  const handleZoom = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.min(Math.max(prev * delta, 0.5), 3));
  };

  const handleInputChange = (section, field, value) => {
    const parsedValue = parseFloat(value) || 0;
    switch(section) {
      case 'dimensiones':
        setDimensiones(prev => ({ ...prev, [field]: parsedValue }));
        break;
      case 'armadura':
        setArmadura(prev => ({ ...prev, [field]: parsedValue }));
        break;
      case 'materiales':
        setMateriales(prev => ({ ...prev, [field]: parsedValue }));
        break;
      case 'cargas':
        setCargas(prev => ({ ...prev, [field]: parsedValue }));
        break;
      case 'suelo':
        setSuelo(prev => ({ ...prev, [field]: parsedValue }));
        break;
      default:
        break;
    }
  };

  // 7. Renderizado
  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Área de dibujo */}
      <div className="w-3/4 flex items-center justify-center" onWheel={handleZoom}>
        <div ref={canvasRef} className="bg-white shadow w-full h-[100vh] overflow-hidden"></div>
      </div>

      {/* Panel de control */}
      <div ref={menuRef} className="w-1/4 p-4 bg-white shadow rounded-lg overflow-y-auto" style={{ maxHeight: '100vh' }}>
        <h2 className="text-xl font-bold mb-4">Diseño de Contrapiso</h2>
        
        {resultados.error && (
          <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">
            {resultados.error}
          </div>
        )}

        {/* Sección de dimensiones */}
        <div className="mb-6">
          <h3 className="font-semibold mb-2">Dimensiones</h3>
          {['ancho', 'espesor', 'recubrimiento'].map((item) => (
            <div key={item} className="mb-4">
              <label className="block text-gray-700">
                {item === 'ancho' ? 'Ancho (m)' : 
                 item === 'espesor' ? 'Espesor (m)' : 'Recubrimiento (cm)'}:
              </label>
              <input
                type="number"
                value={dimensiones[item]}
                onChange={(e) => handleInputChange('dimensiones', item, e.target.value)}
                min={item === 'recubrimiento' ? 2 : item === 'espesor' ? 0.05 : 1}
                max={item === 'recubrimiento' ? 5 : 20}
                step={item === 'recubrimiento' ? 1 : 0.01}
                className="w-full border rounded p-2"
              />
            </div>
          ))}
        </div>

        {/* Sección de armadura */}
        <div className="mb-6">
          <h3 className="font-semibold mb-2">Armadura</h3>
          <div className="mb-4">
            <label className="block text-gray-700">Separación (m):</label>
            <input
              type="number"
              value={armadura.separacion}
              onChange={(e) => handleInputChange('armadura', 'separacion', e.target.value)}
              min="0.10"
              max="0.30"
              step="0.05"
              className="w-full border rounded p-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700">Diámetro (mm):</label>
            <select
              value={armadura.diametro}
              onChange={(e) => handleInputChange('armadura', 'diametro', e.target.value)}
              className="w-full border rounded p-2"
            >
              {[6, 8, 10, 12].map(d => (
                <option key={d} value={d}>{d} mm</option>
              ))}
            </select>
          </div>
        </div>

        {/* Sección de materiales */}
        <div className="mb-6">
          <h3 className="font-semibold mb-2">Materiales</h3>
          {['fc', 'fy'].map((item) => (
            <div key={item} className="mb-4">
              <label className="block text-gray-700">
                {item === 'fc' ? "f'c (kg/cm²)" : "fy (kg/cm²)"}:
              </label>
              <input
                type="number"
                value={materiales[item]}
                onChange={(e) => handleInputChange('materiales', item, e.target.value)}
                min={item === 'fc' ? 175 : 2800}
                max={item === 'fc' ? 350 : 5000}
                step={item === 'fc' ? 5 : 100}
                className="w-full border rounded p-2"
              />
            </div>
          ))}
        </div>

        {/* Sección de cargas */}
        <div className="mb-6">
          <h3 className="font-semibold mb-2">Cargas (kg/m²)</h3>
          {['viva', 'muerta'].map((item) => (
            <div key={item} className="mb-4">
              <label className="block text-gray-700">
                {item === 'viva' ? 'Carga viva' : 'Carga muerta'}:
              </label>
              <input
                type="number"
                value={cargas[item]}
                onChange={(e) => handleInputChange('cargas', item, e.target.value)}
                min="0"
                max={item === 'viva' ? 5000 : 1000}
                step={item === 'viva' ? 50 : 10}
                className="w-full border rounded p-2"
              />
            </div>
          ))}
        </div>

        {/* Sección de suelo */}
        <div className="mb-6">
          <h3 className="font-semibold mb-2">Suelo</h3>
          <div className="mb-4">
            <label className="block text-gray-700">Módulo balasto (kgf/cm³):</label>
            <select
              value={suelo.balasto}
              onChange={(e) => handleInputChange('suelo', 'balasto', e.target.value)}
              className="w-full border rounded p-2"
            >
              {[2, 5, 8, 15].map(k => (
                <option key={k} value={k}>
                  {k} kgf/cm³ ({k < 5 ? 'blando' : k < 8 ? 'medio' : 'firme'})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Resultados */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="font-semibold mb-3 text-lg">Resultados</h3>
          
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-700">Peso propio:</span>
              <span className="font-medium">{resultados.pesoPropio} kg/m²</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-700">Carga total:</span>
              <span className="font-medium">{resultados.cargaTotal} kg/m²</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-700">Momento solicitante:</span>
              <span className="font-medium">{resultados.momentoMaximo} kg-m/m</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-700">Capacidad resistente:</span>
              <span className={`font-medium ${
                resultados.relacionCapacidad >= 1 ? 'text-green-600' : 'text-red-600'
              }`}>
                {resultados.capacidadMomento} kg-m/m
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-700">Relación φMₙ/Mₐ:</span>
              <span className={`font-medium ${
                resultados.relacionCapacidad >= 1 ? 'text-green-600' : 'text-red-600'
              }`}>
                {resultados.relacionCapacidad}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-gray-700">Separación juntas:</span>
              <span className="font-medium">{resultados.separacionJuntas} cm</span>
            </div>
            
            <div className="pt-2 mt-2 border-t border-gray-200">
              <div className={`font-semibold text-center ${
                resultados.relacionCapacidad >= 1 ? 'text-green-600' : 'text-red-600'
              }`}>
                {resultados.estado}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contrapisos;